import React, { useRef, useState, useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { Icons, Menu } from '@nextiva/next-ui-components';
import { dataSelector } from '@nextiva/global-store';
import { PropTypes } from 'prop-types';
import { actionTypes } from 'containers/pages/Teams/redux';

import { createHuntGroup } from 'api/huntGroupApi';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import {
  Item,
  StyledMenuIconButton,
  StyledMenuIcon,
} from 'components/MenuIconButton';
import { useSnackbar, useGetCompanyInfo } from 'hooks';
import { HuntGroupService } from 'services';
import classes from './TeamActions.scss';

const linksConst = {
  EDIT_TEAM: 'editTeam',
  MESSAGING: 'messaging',
  CRM_GROUPS: 'crmGroups',
  TEAM_VOICE_SETTINGS: 'teamVoiceSettings',
  DELETE_TEAM: 'deleteTeam',
};

const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};

const transformOrigin = {
  vertical: 'top',
};

const crmGroupsAction = () => {
  const redirectUrl = `${window.location.origin}/apps/admin/#/departments/manage`;

  window.location.href = redirectUrl;

  return redirectUrl;
};

const TeamActions = React.memo(({ onEditTeam, huntGroup = '', team = {} }) => {
  const {
    featureState: { isClusterIdInBWPatching, teamsSMS },
  } = useContext(FeatureContext);
  const { licenses } = useSelector(dataSelector);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const menuButtonRef = useRef(null);

  const companyInfo = useGetCompanyInfo();

  const [actionsOpen, setActionsOpen] = useState(false);
  const history = useHistory();

  const setSnackbar = useSnackbar();

  const hasVoice = companyInfo.data?.platformVoice || false;

  const redirectToMessaging = () => {
    history.push(`/teams/messaging/${huntGroup}/${team.id}`);
  };

  const createHuntGroupID = (messaging) => {
    const type = messaging ? 'messaging' : 'voice settings';
    dispatch({
      type: actionTypes.SET_BACKDROP,
      payload: {
        message: `One moment while we set up ${type} for team ${team.name}`,
        open: true,
      },
    });

    createHuntGroup({
      assignedProfiles: team.assignedProfileIds,
      name: team.name,
      teamId: team.id,
    })
      .then((res) => {
        queryClient.invalidateQueries(['getTeams']);
        dispatch({
          type: actionTypes.SET_BACKDROP,
          payload: { open: false, message: '' },
        });
        history.push(
          messaging
            ? `/teams/messaging/${res.huntGroup}/${team.name}`
            : `/teams/call-group/${res.huntGroupId}/${team.name}/phone-number`,
        );
      })
      .catch(() => {
        dispatch({
          type: actionTypes.SET_BACKDROP,
          payload: { open: false, message: '' },
        });
        // TODO: if the call fails with a 409 we need to re-fetch the teams list -- occurs when the team already has a hunt group but we dont have that data on the FE
        // This happens if you create the voice settings on the fly but then hit the back button in your browser -- it loads the stale team data rather than fetching new data

        setSnackbar({
          message: `There was a problem creating the ${type} for ${team.name}. Please try again later or contact support if the issue persists.`,
          severity: 'error',
        });
      });
  };

  const onDelete = useCallback(async () => {
    dispatch({
      type: actionTypes.SET_BACKDROP,
      payload: { open: true, message: 'Checking your team' },
    });

    try {
      const {
        numberOfCallFlows,
      } = await HuntGroupService.getCallFlowCountForTeam(team.id);
      dispatch({
        type: actionTypes.SET_DELETE_VOICE_MODAL_INFO,
        payload: { canBeDeleted: numberOfCallFlows === 0, team },
      });
    } catch {
      dispatch({
        type: actionTypes.SET_BACKDROP,
        payload: { open: false, message: '' },
      });
      setSnackbar({
        message: `We were unable to delete voice for team ${team.name}. Please try again later or contact support if the issue persists.`,
        severity: 'error',
      });
    }
  }, []);

  const redirectToTeamVoiceSettings = useCallback(() => {
    if (huntGroup) {
      history.push(`/teams/call-group/${huntGroup}/${team.name}/phone-number`);
    } else {
      createHuntGroupID();
    }
  }, []);

  const options = [
    {
      label: 'Edit Team',
      value: linksConst.EDIT_TEAM,
      onClick: onEditTeam,
    },
    ...(hasVoice && !isClusterIdInBWPatching
      ? [
          {
            label: 'Voice Settings',
            value: linksConst.TEAM_VOICE_SETTINGS,
            className: classes.voiceIcon,
            onClick: redirectToTeamVoiceSettings,
          },
        ]
      : []),
    ...(teamsSMS && huntGroup
      ? [
          {
            label: 'Messaging',
            value: linksConst.MESSAGING,
            onClick: redirectToMessaging,
          },
        ]
      : []),
    ...(licenses
      .find((lic) => lic.product === 'CRM')
      ?.features.includes('CRM_BASE_FEATURE')
      ? [
          {
            label: 'CRM Groups',
            value: linksConst.CRM_GROUPS,
            onClick: crmGroupsAction,
          },
        ]
      : []),
    ...(team.voiceEnabled
      ? [
          {
            label: 'Delete Voice',
            value: linksConst.DELETE_TEAM,
            onClick: onDelete,
          },
        ]
      : []),
  ];

  return (
    <>
      <StyledMenuIconButton
        onClick={() => setActionsOpen(true)}
        data-testid="PlatformAdminUI-TeamActions-MenuIconButton"
        aria-label="Menu Icon Button"
        ref={menuButtonRef}
      >
        <StyledMenuIcon icon={Icons.menu} />
      </StyledMenuIconButton>
      <Menu
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        anchorEl={menuButtonRef.current}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableAutoFocusItem
      >
        {options.map((option) => (
          <Item
            onClick={option.onClick}
            key={option.label}
            data-testid={`PlatformAdminUI-TeamActions-${option.value}`}
          >
            <div className={option.className}>{option.label}</div>
          </Item>
        ))}
      </Menu>
    </>
  );
});

TeamActions.propTypes = {
  onEditTeam: PropTypes.func.isRequired,
  huntGroup: PropTypes.string,
  team: PropTypes.object,
};

export default TeamActions;
